import type { ActionResult, Role, Team } from "@brandwall/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router";
import { ApiError, api, copyText, del, patch, post } from "../api";
import { useWorkspace } from "../components/Shell";
import { useToast } from "../components/Toast";

export function TeamTab() {
  const { ws } = useParams();
  const workspace = useWorkspace();
  const isOwner = workspace.role === "owner";
  const qc = useQueryClient();
  const toast = useToast();
  const team = useQuery({ queryKey: ["team", ws], queryFn: () => api<Team>(`/v1/w/${ws}/team`) });
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("approver");
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invite = async () => {
    setError(null);
    try {
      const r = await post<Team & { result: ActionResult; link: string | null }>(
        `/v1/w/${ws}/invites`,
        { email, role },
      );
      qc.setQueryData(["team", ws], r);
      toast(r.result.summary);
      setLink(r.link);
      setEmail("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Something went wrong.");
    }
  };
  const t = team.data;
  if (!t) return <p className="muted">Loading…</p>;
  return (
    <div className="stack" style={{ maxWidth: 760 }}>
      {isOwner && (
        <section className="card stack">
          <h2>Invite someone</h2>
          {error && <div className="error">{error}</div>}
          <div className="row wrap">
            <input
              className="input spacer"
              style={{ minWidth: 220 }}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={`name@${workspace.website?.replace(/^https?:\/\/(www\.)?/, "").replace(/\/.*$/, "") ?? "example.com"}`}
              aria-label="Email"
            />
            <select
              className="input"
              style={{ width: "auto" }}
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              aria-label="Role"
            >
              <option value="owner">Owner</option>
              <option value="approver">Approver</option>
              <option value="viewer">Viewer</option>
            </select>
            <button type="button" className="btn" disabled={!email.includes("@")} onClick={invite}>
              Send invite
            </button>
          </div>
          <p className="small muted">
            Owners can do everything. Approvers can approve posts and walls. Viewers can look but
            not change anything.
          </p>
          {link && (
            <div className="notice row wrap">
              <span className="spacer" style={{ wordBreak: "break-all" }}>
                Send them this link to join: {link}
              </span>
              <button
                type="button"
                className="btn sm"
                onClick={async () => {
                  await copyText(link);
                  toast("Copied.");
                }}
              >
                Copy link
              </button>
            </div>
          )}
        </section>
      )}
      <section className="card stack">
        {t.members.map((m) => (
          <div key={m.userId} className="row">
            <span className="av">{(m.name ?? m.email)[0]?.toUpperCase()}</span>
            <span className="spacer">
              <b>{m.name ?? m.email}</b>
              {m.you && <span className="small muted"> (you)</span>}
              <div className="small muted">{m.email}</div>
            </span>
            {isOwner && !m.you ? (
              <>
                <select
                  className="input"
                  style={{ width: "auto" }}
                  value={m.role}
                  aria-label={`Role for ${m.email}`}
                  onChange={async (e) => {
                    try {
                      qc.setQueryData(
                        ["team", ws],
                        await patch<Team>(`/v1/w/${ws}/members/${m.userId}`, {
                          role: e.target.value,
                        }),
                      );
                    } catch (err) {
                      toast(err instanceof ApiError ? err.message : "Something went wrong.");
                    }
                  }}
                >
                  <option value="owner">Owner</option>
                  <option value="approver">Approver</option>
                  <option value="viewer">Viewer</option>
                </select>
                <button
                  type="button"
                  className="btn link small"
                  onClick={async () => {
                    if (!window.confirm(`Remove ${m.email} from ${workspace.name}?`)) return;
                    toast((await del<ActionResult>(`/v1/w/${ws}/members/${m.userId}`)).summary);
                    qc.invalidateQueries({ queryKey: ["team", ws] });
                  }}
                >
                  Remove
                </button>
              </>
            ) : (
              <span className="pill" style={{ textTransform: "capitalize" }}>
                {m.role}
              </span>
            )}
          </div>
        ))}
        {t.invites.map((i) => (
          <div key={i.id} className="row">
            <span className="av" style={{ background: "#c9ccc1" }}>
              {i.email[0]?.toUpperCase()}
            </span>
            <span className="spacer">
              <b>{i.email}</b>
              <div className="small muted">
                Invite sent {new Date(i.createdAt).toLocaleDateString("en-GB")}
              </div>
            </span>
            <span className="pill warn" style={{ textTransform: "capitalize" }}>
              Invited {i.role}
            </span>
            {isOwner && (
              <button
                type="button"
                className="btn link small"
                onClick={async () =>
                  qc.setQueryData(["team", ws], await del<Team>(`/v1/w/${ws}/invites/${i.id}`))
                }
              >
                Cancel
              </button>
            )}
          </div>
        ))}
        <p className="small muted">
          {t.seatsUsed} of {t.seats} seat{t.seats === 1 ? "" : "s"} used on{" "}
          {workspace.plan[0]!.toUpperCase() + workspace.plan.slice(1)}
        </p>
      </section>
    </div>
  );
}
